import React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/zeit/45b021a8/node_modules/gatsby-theme-carbon/src/templates/Default.js";
import { StructuredListWrapper, StructuredListHead, StructuredListBody, StructuredListRow, StructuredListInput, StructuredListCell, OrderedList, ListItem } from 'carbon-components-react';
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const PageDescription = makeShortcode("PageDescription");
const AnchorLinks = makeShortcode("AnchorLinks");
const AnchorLink = makeShortcode("AnchorLink");
const Row = makeShortcode("Row");
const Column = makeShortcode("Column");
const UnorderedList = makeShortcode("UnorderedList");
const Caption = makeShortcode("Caption");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <PageDescription mdxType="PageDescription">
      <p>{`Search is an essential pattern for navigation or discovery. We live in the age of search, where a search engine is often the primary entry point into the internet. User expectations are high for the way search should work, and consistency is critical.`}</p>
    </PageDescription>
    <AnchorLinks mdxType="AnchorLinks">
  <AnchorLink mdxType="AnchorLink">Overview</AnchorLink>
  <AnchorLink mdxType="AnchorLink">Basic search</AnchorLink>
  <AnchorLink mdxType="AnchorLink">Active search</AnchorLink>
  <AnchorLink mdxType="AnchorLink">Focused search</AnchorLink>
  <AnchorLink mdxType="AnchorLink">Accessibility</AnchorLink>
  <AnchorLink mdxType="AnchorLink">Related</AnchorLink>
  <AnchorLink mdxType="AnchorLink">References</AnchorLink>
  <AnchorLink mdxType="AnchorLink">Feedback</AnchorLink>
    </AnchorLinks>
    <h2>{`Overview`}</h2>
    <p>{`Searching is an intuitive method of discovery, offering users a way to explore a website or application using keywords. Most searches begin broad, with the scope narrowing as filters are applied.`}</p>
    <p>{`The method of search you use depends on the size of the data set being searched and the location of the search within your product.`}</p>
    <p>{`The placement of your search field depends on the construction of your application and the scope of the search. For global searches, see the `}<a parentName="p" {...{
        "href": "/patterns/global-header-pattern"
      }}>{`global header pattern`}</a>{` for placement guidance. See the `}<a parentName="p" {...{
        "href": "/components/data-table/usage#search"
      }}>{`data table`}</a>{` component for search placement within a data table.`}</p>
    <h3>{`Anatomy`}</h3>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "56.25%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAALCAYAAAB/Ca1DAAAACXBIWXMAAAsSAAALEgHS3X78AAAA5UlEQVQoz41Tyw7CIBDk///LkwdPHjwYDyaamDSCLK8qjFKhhVpU6GQ3y3RfLAwLK4QwypbeWqx1UP7U0psOc/QK0e7DgJHjk32JnwKxpTKyrrdH2MNl0N3pCtocvmYZ7awizKJ57eCVe+umhyf7wWtmyAWHEAJKK5AiSCkh5A1XzsEzxCQjh9SLSwRtTOEwpWulguESprvBChpgIqKtgO7EYB85L5sjPTkcS07Np/Ue/Zmn0soGvYVc7YrSF3pYzVnslbsjPPx0m3nnc9vXt13s+LH5ILeGu2x8a9Sqwf71Iv59MU+k4GTLh+kg0wAAAABJRU5ErkJggg==')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Anatomy of a basic search",
            "title": "Anatomy of a basic search",
            "src": "/static/6856c18f70cc8837f08eab81bb8b3121/3cbba/anatomy.png",
            "srcSet": ["/static/6856c18f70cc8837f08eab81bb8b3121/7fc1e/anatomy.png 288w", "/static/6856c18f70cc8837f08eab81bb8b3121/a5df1/anatomy.png 576w", "/static/6856c18f70cc8837f08eab81bb8b3121/3cbba/anatomy.png 1152w", "/static/6856c18f70cc8837f08eab81bb8b3121/392b1/anatomy.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>
    <ol>
      <li parentName="ol"><strong parentName="li">{`Scope filter (optional):`}</strong>{` The optional scoped search allow users to limit their searches to a section or category of content on a website.`}</li>
      <li parentName="ol"><strong parentName="li">{`Search icon:`}</strong>{` Signifies a search field. The magnifying glass icon is a universal way to indicate search.`}</li>
      <li parentName="ol"><strong parentName="li">{`Placeholder text:`}</strong>{` Useful and short text hinting at what the user can search for. For example, “Search for networks or devices.”`}</li>
      <li parentName="ol"><strong parentName="li">{`Text entry field:`}</strong>{` The place where a user enters their search query.`}</li>
    </ol>
    <h3>{`Types of search`}</h3>
    <Row mdxType="Row">
  <Column noGutterSm mdxType="Column">
    <StructuredListWrapper mdxType="StructuredListWrapper">
      <StructuredListHead mdxType="StructuredListHead">
        <StructuredListRow head mdxType="StructuredListRow">
          <StructuredListCell head mdxType="StructuredListCell">Type</StructuredListCell>
          <StructuredListCell head mdxType="StructuredListCell">When to use</StructuredListCell>
          <StructuredListCell head mdxType="StructuredListCell">Use cases</StructuredListCell>
        </StructuredListRow>
      </StructuredListHead>
      <StructuredListBody mdxType="StructuredListBody">
        <StructuredListRow mdxType="StructuredListRow">
          <StructuredListCell mdxType="StructuredListCell">
            <a href="#basic-search">Basic search</a>
          </StructuredListCell>
          <StructuredListCell mdxType="StructuredListCell">
            When a user will conduct a search and be routed to a distinct
            results page.
          </StructuredListCell>
          <StructuredListCell mdxType="StructuredListCell">
            Global searches, or any search that routes users to a distinct
            results page
          </StructuredListCell>
        </StructuredListRow>
        <StructuredListRow mdxType="StructuredListRow">
          <StructuredListCell mdxType="StructuredListCell">
            <a href="#active-search">Active search</a>
          </StructuredListCell>
          <StructuredListCell mdxType="StructuredListCell">
            Best for small data sets, like a single page, website, or table.
            When a user will benefit from constant feedback based on their
            search query, and when the server can handle a substantial search
            query load.
          </StructuredListCell>
          <StructuredListCell mdxType="StructuredListCell">
            Actively searches a database and lists the top results in results
            panel or on the current page. For example:
            <UnorderedList mdxType="UnorderedList">
              — Searching a catalog
              <br />
              — Searching a small website
              <br />
              — Searching a small data set within a table
              <br />— Searching a subset of information embedded within a product
              or application’s page
            </UnorderedList>
          </StructuredListCell>
        </StructuredListRow>
        <StructuredListRow mdxType="StructuredListRow">
          <StructuredListCell mdxType="StructuredListCell">
            <a href="#focused-search">Focused search</a>
          </StructuredListCell>
          <StructuredListCell mdxType="StructuredListCell">
            When the search is focused on tasks or information specific to the
            active user. Limits server load by focusing search scope while
            offering the power of a broad, basic search.
          </StructuredListCell>
          <StructuredListCell mdxType="StructuredListCell">
            <UnorderedList mdxType="UnorderedList">
              — Assigned tasks
              <br />
              — Product catalogs
              <br />
              — Personal repositories
              <br />
              — Work created by the active user
              <br />
            </UnorderedList>
          </StructuredListCell>
        </StructuredListRow>
      </StructuredListBody>
    </StructuredListWrapper>
  </Column>
    </Row>
    <h3>{`Best practices`}</h3>
    <h4>{`Avoid dead ends`}</h4>
    <p>{`If a search returns “No results,” suggest a follow-up action. Provide suggestions and helpful resources to aid the user in finding what they are looking for. For more on no results for searches, see the `}<a parentName="p" {...{
        "href": "/patterns/empty-states-pattern"
      }}>{`empty states pattern`}</a>{`.`}</p>
    <h4>{`Include a loading indicator`}</h4>
    <p>{`If the search will take longer than a moment or two, include a loading indicator. Your loading state should reflect your empty state with useful helper text signaling that the search is still ongoing. Include a progress bar for advanced, resource-intensive searches. Let the user know how far along the search is, and roughly how long it will take to finish.`}</p>
    <h4>{`Display the number of results`}</h4>
    <p>{`Always include the number of search results, including for searches with no results. If you plan to offer a scope filter, also include the number of results for each scope selector.`}</p>
    <h4>{`Don’t include a label`}</h4>
    <p>{`Avoid adding a label to your search field. Users expect and understand search fields, and no label is necessary. A search icon along with useful placeholder text should clearly indicate that the field is intended for search.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "34.78260869565217%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAHCAYAAAAIy204AAAACXBIWXMAAAsSAAALEgHS3X78AAAAfElEQVQoz62QwQrDIBBE/f9f6zX3nHoIRJdEJcFlnXQDK20ILTQZeIvj6IC6x9Chn56gdca40GUcrRNYGHfJ2UJEIFWpqG+o37NvvO7ZeadDFWOE9x4hhIZ6IkJKac+P2H7OGdbTCpkZpZRTNPtFK9Rh5qo+nlwPf/cPqg2xjCYCVmVVwAAAAABJRU5ErkJggg==')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Provide useful placeholder text and include a search icon",
            "title": "Provide useful placeholder text and include a search icon",
            "src": "/static/a929decd9e92483104b045af9e01bfb6/3cbba/2_do.png",
            "srcSet": ["/static/a929decd9e92483104b045af9e01bfb6/7fc1e/2_do.png 288w", "/static/a929decd9e92483104b045af9e01bfb6/a5df1/2_do.png 576w", "/static/a929decd9e92483104b045af9e01bfb6/3cbba/2_do.png 1152w", "/static/a929decd9e92483104b045af9e01bfb6/392b1/2_do.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
        <Caption mdxType="Caption">Provide useful placeholder text and include a search icon.</Caption>
      </Column>
    </Row>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "34.78260869565217%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAHCAYAAAAIy204AAAACXBIWXMAAAsSAAALEgHS3X78AAAAcElEQVQoz62QwQqAMAxD9/8/5cXvEO86FLaps1sjLWzgSXALvOZSAom5hhH3NCNtO2hZQattwrAPQGYw+sjIkbDzOJBSAuf8D2bFyBE552CtVffeq39R/kIIKDk1kIgQY1T/Qw3UytxnwVflskELogdEOyZtH2YrzQAAAABJRU5ErkJggg==')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Don’t include a label for search fields.",
            "title": "Don’t include a label for search fields.",
            "src": "/static/daf711c5df612ad94ce950ef96b95fa3/3cbba/2_dont.png",
            "srcSet": ["/static/daf711c5df612ad94ce950ef96b95fa3/7fc1e/2_dont.png 288w", "/static/daf711c5df612ad94ce950ef96b95fa3/a5df1/2_dont.png 576w", "/static/daf711c5df612ad94ce950ef96b95fa3/3cbba/2_dont.png 1152w", "/static/daf711c5df612ad94ce950ef96b95fa3/392b1/2_dont.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
        <Caption mdxType="Caption">Don’t include a label for search fields.</Caption>
      </Column>
    </Row>
    <h4>{`Localize the search field`}</h4>
    <p>{`For languages that read right to left, flip the layout of the search box as shown in the next example.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "34.78260869565217%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAHCAYAAAAIy204AAAACXBIWXMAAAsSAAALEgHS3X78AAAAdElEQVQoz62PwQoDIQxE/f9f22vvPfWwUJWsYsWosyhGKPSwSx14ZJLAkKhtf+BJL9h44P2xf6NsJHBhrJISU0q5Rx2MvtbaUWJCCHDOwXvf6y9kp7WGMWZWIuoZ88LW5JzBzJdIKX3RZjNQzAr1l8WsoOkEzKImEzKSXrEAAAAASUVORK5CYII=')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Flip the layout of the search box for languages that read right to left instead of left to right",
            "title": "Flip the layout of the search box for languages that read right to left instead of left to right",
            "src": "/static/24f6c6a3efbb3f778a65ac00e7d29c2d/3cbba/do_hebrew.png",
            "srcSet": ["/static/24f6c6a3efbb3f778a65ac00e7d29c2d/7fc1e/do_hebrew.png 288w", "/static/24f6c6a3efbb3f778a65ac00e7d29c2d/a5df1/do_hebrew.png 576w", "/static/24f6c6a3efbb3f778a65ac00e7d29c2d/3cbba/do_hebrew.png 1152w", "/static/24f6c6a3efbb3f778a65ac00e7d29c2d/392b1/do_hebrew.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
        <Caption mdxType="Caption">
  Example of a search box for languages that read right to left instead of left
  to right
        </Caption>
      </Column>
    </Row>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "34.78260869565217%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAHCAYAAAAIy204AAAACXBIWXMAAAsSAAALEgHS3X78AAAAdklEQVQoz63Q0QqEIBAFUP//p3rpO5beS3cZFcLJuetECkEPkV04AypcGM06jEifCdv3B54X8GK7GAkRyBlvxegQVUpzIU+J7IwODXmCc66x1oKIEEKA9/5SfYsxova0QmZGSulE7+5qhfvKx6E3p5XrH/TQ/AEpIyZN+OqQMgAAAABJRU5ErkJggg==')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Flip the layout of the search box for languages that read right to left instead of left to right",
            "title": "Flip the layout of the search box for languages that read right to left instead of left to right",
            "src": "/static/91e3e60fe06f7da289ad7e71f3b9110b/3cbba/dont_hebrew.png",
            "srcSet": ["/static/91e3e60fe06f7da289ad7e71f3b9110b/7fc1e/dont_hebrew.png 288w", "/static/91e3e60fe06f7da289ad7e71f3b9110b/a5df1/dont_hebrew.png 576w", "/static/91e3e60fe06f7da289ad7e71f3b9110b/3cbba/dont_hebrew.png 1152w", "/static/91e3e60fe06f7da289ad7e71f3b9110b/392b1/dont_hebrew.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
        <Caption mdxType="Caption">
  The search icon is recognized and acceptable across languages and countries.
        </Caption>
      </Column>
    </Row>
    <h4>{`Add a scope filter to your search field`}</h4>
    <p>{`The optional scoped search allows users to limit their searches to a section or category of content on a website, as opposed to a global search that searches everything at once. Only one scoped category can be selected at a time, but the dropdown should always include an “All” or “Any” option, and this option should be selected by default. With “All” or “Any” selected, the field will function as a `}<a parentName="p" {...{
        "href": "#basic-search"
      }}>{`basic global search`}</a>{`.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "34.78260869565217%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAHCAYAAAAIy204AAAACXBIWXMAAAsSAAALEgHS3X78AAAAW0lEQVQoz62RUQoAIQhEvf9l06yfBF0mcGnZ/mrgMQXDoEXuHhD8FIjWy6nQQ7em+0wIMfOktRaqGrXWEJEopWxBJrO993+hmcUYY/p6hu/IXPIW5srX3vD2Lz9xwCnDF39hWQAAAABJRU5ErkJggg==')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Example of a scope filter on a search field",
            "title": "Example of a scope filter on a search field",
            "src": "/static/3c7ffc0fc50fbdabe4a707e5d45ff2a0/3cbba/5.png",
            "srcSet": ["/static/3c7ffc0fc50fbdabe4a707e5d45ff2a0/7fc1e/5.png 288w", "/static/3c7ffc0fc50fbdabe4a707e5d45ff2a0/a5df1/5.png 576w", "/static/3c7ffc0fc50fbdabe4a707e5d45ff2a0/3cbba/5.png 1152w", "/static/3c7ffc0fc50fbdabe4a707e5d45ff2a0/392b1/5.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
        <Caption mdxType="Caption">Example of a scope filter on a search field</Caption>
      </Column>
    </Row>
    <h2>{`Basic search`}</h2>
    <p>{`Basic search entails any search that directs users to a distinct results page. In these instances, offer either trending searches that match the active user’s keywords, recent searches by the active user, or both.`}</p>
    <p>{`A basic search does not actually query a data set until the user runs the search.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "34.78260869565217%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAHCAYAAAAIy204AAAACXBIWXMAAAsSAAALEgHS3X78AAAAUUlEQVQoz9WQOwrAQAhEvf9d1UJtFDQYcIskkCLbZODhZ2AQITOr1fUrLZiwHeoc2H7hNCJSiFhEtOiZmUtVT//K7M3sHhgR5e6PtPfGf354AHziKdJ8twKqAAAAAElFTkSuQmCC')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Example of basic search",
            "title": "Example of basic search",
            "src": "/static/894dd733a6fe30e25df88df8538f0870/3cbba/basic_search.png",
            "srcSet": ["/static/894dd733a6fe30e25df88df8538f0870/7fc1e/basic_search.png 288w", "/static/894dd733a6fe30e25df88df8538f0870/a5df1/basic_search.png 576w", "/static/894dd733a6fe30e25df88df8538f0870/3cbba/basic_search.png 1152w", "/static/894dd733a6fe30e25df88df8538f0870/392b1/basic_search.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
        <Caption mdxType="Caption">Example of a basic search field</Caption>
      </Column>
    </Row>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`When to use`}</th>
          <th parentName="tr" {...{
            "align": null
          }}></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`When searching is expensive`}</em></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Searching a large body of information can be resource intensive and expensive, expecially in instances where many users are searching the information simultaneously. Actively running the search after every character is entered or removed can be impractical.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`When searching is slow`}</em></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`If the body of information is massive, actively searching can become too slow to be practical. Extended processing time following each character entered results in an application or website seeming unresponsive.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`When the information is unfamiliar`}</em></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`When users are searching an application or website they’re unfamiliar with, a dedicated search results page can give them a better idea of data structure and available resources.`}</td>
        </tr>
      </tbody>
    </table>
    <h3>{`Behaviors`}</h3>
    <h4>{`Recent search suggestions`}</h4>
    <p>{`When a user clicks or tabs into a search field, they are be shown a menu of either the user’s recent searches, trending searches, or both. When the user types in the field, this menu is replaced with suggestions based on the search query.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "56.25%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAALCAYAAAB/Ca1DAAAACXBIWXMAAAsSAAALEgHS3X78AAAA4ElEQVQoz62Pi07DMAxF+/+fWfWxZCRZnmVt1oudkQoECxNQ6dROY5/aHbBj3wGOf6E6uppwzLffcfvg6Djhj71ImM4RQl9xUgvx+iQLJnXFlt8n5Bf/YZIOkzAQLw5nHSCVfx4daagyJq98T6w1kOJUcPaClCJi8IgxtKGahWqrpyuD0sE5h3me0fc9hnHExVr4EOC9J0IDjxAeCIUQkFKWqLWmwtoQHsL3MX4jtDSRUqpcppRKbIl+FNaVh2HASCvz6saYo6kF13wR5pyxbdvBuq6fzi249xDW5D8edr0BtS5YVmUM9j0AAAAASUVORK5CYII=')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Example of recent search suggestions",
            "title": "Example of recent search suggestions",
            "src": "/static/b72cc4f578ccb1d999b7206ebc8781a8/3cbba/6.png",
            "srcSet": ["/static/b72cc4f578ccb1d999b7206ebc8781a8/7fc1e/6.png 288w", "/static/b72cc4f578ccb1d999b7206ebc8781a8/a5df1/6.png 576w", "/static/b72cc4f578ccb1d999b7206ebc8781a8/3cbba/6.png 1152w", "/static/b72cc4f578ccb1d999b7206ebc8781a8/392b1/6.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>
    <h4>{`Type-ahead suggestions`}</h4>
    <p>{`Type-ahead suggestions are generated from ongoing analyses of use search patterns, and place minimal load on servers. Without committing to a search, the user can see a preview of results, suggestions of additional search terms, or zero in on what they were looking for.`}</p>
    <p>{`Type-ahead suggestions appear as soon as the user begins typing in the search field.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "56.25%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAALCAYAAAB/Ca1DAAAACXBIWXMAAAsSAAALEgHS3X78AAAA5ElEQVQoz62Q227DIBAF/f9/WKl9MHXFzU5iw4JpAycQ4iRKW/shWWlgJTijhQZISCmvKT0JUFzN0jxfVdosjQ8R5I9wc4QLKbPsW8ScvZuwLD8RaLnFRyvQiT3kQBC9rQz/cDmTvUErDL6PV2H9v92B8PbOwL40+j1BDQZqZ1fRZc/SIWdjHfEmdI7wyVpIwTGOBziyIGtAtEa9U7LpUUhEYIyBcw4pFZRSsNbCFMwaBpb+EJZw13UQQpxlUkporc+BLUr2lzCEkJ86YpqmOtnlsvd+k3meb8KleUUV13XCV1DqBNI6WoREFZvRAAAAAElFTkSuQmCC')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Example of type-ahead suggestions",
            "title": "Example of type-ahead suggestions",
            "src": "/static/2e7cac0e22c62bfb6e2b8a35bcf13d46/3cbba/7.png",
            "srcSet": ["/static/2e7cac0e22c62bfb6e2b8a35bcf13d46/7fc1e/7.png 288w", "/static/2e7cac0e22c62bfb6e2b8a35bcf13d46/a5df1/7.png 576w", "/static/2e7cac0e22c62bfb6e2b8a35bcf13d46/3cbba/7.png 1152w", "/static/2e7cac0e22c62bfb6e2b8a35bcf13d46/392b1/7.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
        <Caption mdxType="Caption">Example of type-ahead suggestions</Caption>
      </Column>
    </Row>
    <h4>{`Results page`}</h4>
    <p>{`Once the user runs the search, they are directed to a distinct results page. On this page, the user should have the option to select filters to narrow their search results. If you’d prefer to offer filters before the user searches, consider adding a scope filter.`}</p>
    <p>{`The design of the results page will depend largely on the needs of your product, and more specific design guidance for a search results page will be covered in future pattern guidance.`}</p>
    <h2>{`Active search`}</h2>
    <p>{`An active search is a rapid way to search an application or data set. The search runs after each character is entered, and results are shown immediately below the search field. You can think of active search as a way to filter a dataset through keywords.`}</p>
    <p>{`An active search is constructed from a search field, a search icon, and an X that serves as a clear or cancel button. There is no Search or Go button, as the search is run actively and the user is not routed to a results page.`}</p>
    <span {...{
      "className": "gatsby-resp-image-wrapper",
      "style": {
        "position": "relative",
        "display": "block",
        "marginLeft": "auto",
        "marginRight": "auto",
        "maxWidth": "1152px"
      }
    }}>{`
      `}<span parentName="span" {...{
        "className": "gatsby-resp-image-background-image",
        "style": {
          "paddingBottom": "64.28571428571428%",
          "position": "relative",
          "bottom": "0",
          "left": "0",
          "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAYAAACpUE5eAAAACXBIWXMAAAsSAAALEgHS3X78AAABkElEQVQ4y62TzW6kMBCEedR9iyTzgzTSXJJzDvk57XkfIrc8x2YTRRMFmMEGY2Ob2i4TkMIhlwTpA2Hoquo2ZPjhI4sx4u7uFuvVCnm+xXa7xWazmVmv11+yEVZSu9/vURQFsmGIuLm9x/kqx5lwfnGRXtjtdok8z5PJkpzGiTxxeXWFsiyRMWbvA5Ru0JoOfd9/r2We/j2/4OnpL94OB5RVJU4VCkE3LTSNWgPnetgFNNfGo1QBR2URQhgFtdao6xpN0ySBQ9miqi2aDlAmJnQ3fKI2A0IE/jw6/Lo2+P1gRClwhoMItVBKJSbR0+kEYwz4fAQLhtRiUdV4fTviWDeIcRZsZsG2lTblWsiA2aq1dhbmF0G4keN1gPecudwHP7a8FEwJpdf3k0PnApwIKqU/kmuZpUt0XZdEacZZklmQqUZBjVYKjfNpTlG6Ct6nYsJiFpHpa5iEPgnSnZsyJmQah/da0vkoaWxanzaO6VhMA9bSZFqbBTmnKQVhm9a6uT2b7kemtSV8xhFkP/0v/wfis8R8nN7RVgAAAABJRU5ErkJggg==')",
          "backgroundSize": "cover",
          "display": "block"
        }
      }}></span>{`
  `}<img parentName="span" {...{
        "className": "gatsby-resp-image-image",
        "alt": "Example of an active search field",
        "title": "Example of an active search field",
        "src": "/static/49f614ddcb90ad5412f23e5932313ec5/3cbba/active_search.png",
        "srcSet": ["/static/49f614ddcb90ad5412f23e5932313ec5/7fc1e/active_search.png 288w", "/static/49f614ddcb90ad5412f23e5932313ec5/a5df1/active_search.png 576w", "/static/49f614ddcb90ad5412f23e5932313ec5/3cbba/active_search.png 1152w", "/static/49f614ddcb90ad5412f23e5932313ec5/0b124/active_search.png 1728w", "/static/49f614ddcb90ad5412f23e5932313ec5/99571/active_search.png 2240w"],
        "sizes": "(max-width: 1152px) 100vw, 1152px",
        "style": {
          "width": "100%",
          "height": "100%",
          "margin": "0",
          "verticalAlign": "middle",
          "position": "absolute",
          "top": "0",
          "left": "0"
        },
        "loading": "lazy"
      }}></img>{`
    `}</span>
    <Caption mdxType="Caption">Example of an active search field</Caption>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`When to use`}</th>
          <th parentName="tr" {...{
            "align": null
          }}></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`When data is limited`}</em></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Active searches should be used in instances where the search is likely to return an accurate result, like a small website or data table with limited data. You can also use active search when looking through a catalog of items, like a product catalog or a library.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`When users know what to look for`}</em></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`If users are familiar with a website or application, search will likely be used to find a resource the user knows exists. Actively returning results in the search results panel allows them to navigate directly to the page or resource.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`When filtering an on-page catalog`}</em></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`If your product presents a catalog on a page, you can use an active search to refine options based on the query.`}</td>
        </tr>
      </tbody>
    </table>
    <h3>{`Behaviors`}</h3>
    <h4>{`Search field`}</h4>
    <p>{`Active search placeholder text should be more specific than a basic search and should remain visible until the user begins typing in the search field. You can optionally add a “See all results” item to your results panel, which when clicked behaves as a `}<a parentName="p" {...{
        "href": "#basic-search"
      }}>{`basic search`}</a>{`.`}</p>
    <p>{`If you are using active search for a catalog or library, refine the on-page results as the user types.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "63.04347826086957%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAYAAACpUE5eAAAACXBIWXMAAAsSAAALEgHS3X78AAAA80lEQVQ4y62T0W6EIBBF/f8f7Es3sRsUEd1VHESL9XaGTbvbVtuHSnIYgzOHIYEMB49sXYGVp/8iQ2LG4ZDOPqSpw44imi7g4mb0fkl0tKT1W9wipryLewWFt3uHC3+/VB5PzwVO5wbKEM665zigqAmKKTZI65aQlz3Mdb4LZRpDQFFopoRtWlSVQdteMQwE5wY4iXvw/3n+LhxHFohII89z1HXNsgFEf0Ep74cwcIdKqYQxhsUVrLXw3qei39gUSqEItNafwrIsU7fSuRTt4ZzDNE1fhbKDHFvEj0iyrEvBHnK6GOPjPcTR93A9+qUc+/TeAStx98Zh032GAAAAAElFTkSuQmCC')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Example of an active search field",
            "title": "Example of an active search field",
            "src": "/static/38eb56128e250b80f99db585c228f5f7/3cbba/11.png",
            "srcSet": ["/static/38eb56128e250b80f99db585c228f5f7/7fc1e/11.png 288w", "/static/38eb56128e250b80f99db585c228f5f7/a5df1/11.png 576w", "/static/38eb56128e250b80f99db585c228f5f7/3cbba/11.png 1152w", "/static/38eb56128e250b80f99db585c228f5f7/392b1/11.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
        <Caption mdxType="Caption">Example of an active search field</Caption>
      </Column>
    </Row>
    <h4>{`Presenting results`}</h4>
    <p>{`Active search is intended to be a quicker search-in-place experience. The user should find what they’re looking for in the results panel. Recent searches from the active user should appear in a panel below the search field and should be replaced by matching search results when the user begins typing.`}</p>
    <p>{`You can also implement active search for a small set of information displayed on a full page, like a catalog of offerings. Before the search is run, all items should be displayed. Once the user begins typing, only show items that match the user’s search.`}</p>
    <h2>{`Focused search`}</h2>
    <p>{`A focused search is a combination of basic search and active search. The user is `}<a parentName="p" {...{
        "href": "#active-search"
      }}>{`actively shown results`}</a>{` within their immediate page, product, or scope. Below those results, they have the option to widen their scope to search all available data.`}</p>
    <p>{`Focused search is ideal for users who are using a specific tool that is part of a catalog or suite.`}</p>
    <span {...{
      "className": "gatsby-resp-image-wrapper",
      "style": {
        "position": "relative",
        "display": "block",
        "marginLeft": "auto",
        "marginRight": "auto",
        "maxWidth": "1152px"
      }
    }}>{`
      `}<span parentName="span" {...{
        "className": "gatsby-resp-image-background-image",
        "style": {
          "paddingBottom": "64.28571428571428%",
          "position": "relative",
          "bottom": "0",
          "left": "0",
          "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAYAAACpUE5eAAAACXBIWXMAAAsSAAALEgHS3X78AAACa0lEQVQ4y62Sy2sTURTG+29YEvOoaRMTQmjNqhtddeu2CEaRIhiRIBSENovYtAuhRbSRQv4ad4ILQboR0lZIjNrJJPPozNy58/g858YUC+LKC4e5jzO/+33n3Dn85zEXRRGOjo6wtbWFZrOJ7e3tK8H7HAcHB+h0Ojg8PFQxm3c67/Dm9T72X7UxHo+nwG63i5c7O2i329jd3VWxt7enotVqqfXa2hpSqRQWFhaQyWSQTqenkbqO/PIdPH7xFqZBQJYZRTFcz/unlc3NTSwuLqJSqaBYLCKfz6NQuIncjQxu363j/RlnxZiL6eOYJsa6DuvCgy9DeK4LTdOg056geUwu6vU6stksSqUScrmcghcImkgksfGoRpQAUgYEjGMIUmdMJjBsF54EwjCESyDTsvCDLuNLG40GlpaWUK1WUS6Xp8BCgYAJ1GoPVE4QMJCmkn626EfLESCB6kDSRR6Bh44DQclPSWEymVQwtsu1ZKXz89ewfu8+goiFEJDRrmdTh3SMLQER0AEBTdsmxTbZkKqGzxrPUSyVsbq6iuWVFVXLW/Rl1Q83nighCqgs+wK2bRHEuzwwDAsOqeNzHh+Ov+Pj5x5OT3ro9wcYDAbonX3Fp+MTfDkdkihBzQ2nQN/3SYmPiEC8KWhtu1IVmZ+V5BJQ4Ddc9TOOEFBE/EqUS0/lqmdjXkzw7XyCiR0ohZKAmnauVLNlXhv6GP2hRmXw1J5pO/g5MmGaBtVcqj1upqqhCDw4fgRHxAjCmJTS06EbuTkcnCiEwIig2kiHS6WQlGe7AS6ozuyQc5TCGDMb8WWws/gPe38fs/yr4xdsCGWCUx9vRQAAAABJRU5ErkJggg==')",
          "backgroundSize": "cover",
          "display": "block"
        }
      }}></span>{`
  `}<img parentName="span" {...{
        "className": "gatsby-resp-image-image",
        "alt": "Example of focused search",
        "title": "Example of focused search",
        "src": "/static/7492b0d9a261e207193ddfc7a306589e/3cbba/focused_search_full.png",
        "srcSet": ["/static/7492b0d9a261e207193ddfc7a306589e/7fc1e/focused_search_full.png 288w", "/static/7492b0d9a261e207193ddfc7a306589e/a5df1/focused_search_full.png 576w", "/static/7492b0d9a261e207193ddfc7a306589e/3cbba/focused_search_full.png 1152w", "/static/7492b0d9a261e207193ddfc7a306589e/0b124/focused_search_full.png 1728w", "/static/7492b0d9a261e207193ddfc7a306589e/99571/focused_search_full.png 2240w"],
        "sizes": "(max-width: 1152px) 100vw, 1152px",
        "style": {
          "width": "100%",
          "height": "100%",
          "margin": "0",
          "verticalAlign": "middle",
          "position": "absolute",
          "top": "0",
          "left": "0"
        },
        "loading": "lazy"
      }}></img>{`
    `}</span>
    <Caption mdxType="Caption">Example of a focused search with results panel</Caption>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`When to use`}</th>
          <th parentName="tr" {...{
            "align": null
          }}></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`Within a product suite`}</em></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A focused search can actively present results related to the active user. This limits the resources required for the search while still giving users the option to widen the scope of the search to an entire product catalog or database.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`Within a folder or data set`}</em></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Focused searches are ideal for searching within a folder or bucket, as the user can either further focus or expand the search through a data structure. If the results panel doesn’t deliver what the user needs, they can choose to search elsewhere without navigating away from the search field.`}</td>
        </tr>
      </tbody>
    </table>
    <h3>{`Behaviors`}</h3>
    <h4>{`Search field`}</h4>
    <p>{`A focused search queries for results related to the search’s focus, whether that is a user, a category, or a data set. The search results are presented in a panel just like in `}<a parentName="p" {...{
        "href": "#active-search"
      }}>{`active search`}</a>{`, but below the results an option to widen the scope and see a full page of search results is included.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "82.06521739130434%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAQCAYAAAAWGF8bAAAACXBIWXMAAAsSAAALEgHS3X78AAABY0lEQVQ4y62T246CMBRF/f9PnGQeALmUXmkpOqjd01OFMAI6DzZZOSeYLvZp8YC4Qgi4BeB6Ay7XgMvt0b9g/v1676OEVDjELsm+S4usMmDqB7UYFpz2kScw6VGwAeM1RbsLSV4xg6LiKBsFJiyE9uC6B1c9WulWcOWizOGrcMiZj6EeCcOjsV1MxxoI3kJrBaVkojMavneb9BFtqHqEpZB6Yzocj0dUVR2FBpzziIDSGq7v4dw2Ik7Q2SchLR03MsbQtm2SdV0H7318O210m1jr4vk5SOPTpYSlUCmVhESe56laa9PGPSmNPPjIsJOwLMuUkHpKKYRYbO7nuuT+bEMopUyypmnm0ekFWZal8bdSTs/oaFZCSlPXdYJkNHZRFH9kz+k2hdM/5dXhv2NXSOdG40516l/JaAJjzLaQxp4+m+nTeZeOZHSJK+E4jik6MQxDqufz+d+sLuUTi1xzwk9A6xfahdY4sjPPIwAAAABJRU5ErkJggg==')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Example of focused search",
            "title": "Example of focused search",
            "src": "/static/3f769e231e1fd3669f79b0dd027a90d3/3cbba/11_focused_search.png",
            "srcSet": ["/static/3f769e231e1fd3669f79b0dd027a90d3/7fc1e/11_focused_search.png 288w", "/static/3f769e231e1fd3669f79b0dd027a90d3/a5df1/11_focused_search.png 576w", "/static/3f769e231e1fd3669f79b0dd027a90d3/3cbba/11_focused_search.png 1152w", "/static/3f769e231e1fd3669f79b0dd027a90d3/392b1/11_focused_search.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
        <Caption mdxType="Caption">Example of focused search</Caption>
      </Column>
    </Row>
    <p>{`If the user chooses a subcategory from the results panel, the search functions like a `}<a parentName="p" {...{
        "href": "#basic-search"
      }}>{`basic search`}</a>{` with a filter applied. Any selected filter should persist during future searches until the user either discards the filter or begins a new session.`}</p>
    <h4>{`Presenting results`}</h4>
    <p>{`When the user clicks or tabs into the focused search field, a menu appears with recent searches from the active user, suggestions specific to the user or session, or both. When the user begins typing, the suggestions disappear and the results are refined, following each character entered.`}</p>
    <p>{`You can also cluster active results by category with the option to “View all” within the category. This is ideal for a user who may be searching within several similar data sets without knowing where the information lives. If the user is likely to know the location of their data, offer a scope filter.`}</p>
    <p>{`A successful focused search will route a user directly to a page or resource. In the event the user does select “See all results,” present a `}<a parentName="p" {...{
        "href": "basic-search"
      }}>{`results page`}</a>{`. Always include the number of search results, including for searches with no results.`}</p>
    <h2>{`Accessibility`}</h2>
    <h3>{`Search`}</h3>
    <p>{`Users should be able to `}<inlineCode parentName="p">{`TAB`}</inlineCode>{` into the input field of the search box to begin typing and press `}<inlineCode parentName="p">{`ENTER`}</inlineCode>{` to run the search query.`}</p>
    <p>{`If using a scope dropdown and/or type-ahead suggestions, those should also be navigable by keyboard. For scope, `}<inlineCode parentName="p">{`TAB`}</inlineCode>{` should select the dropdown and the `}<inlineCode parentName="p">{`ARROW`}</inlineCode>{` keys should open it to cycle through the menu. `}<inlineCode parentName="p">{`ENTER`}</inlineCode>{` should make a selection from the dropdown and advance the user to the input field of the search box.`}</p>
    <p>{`For type-ahead suggestions, the `}<inlineCode parentName="p">{`ARROW`}</inlineCode>{` keys should cycle through displayed suggestions, with `}<inlineCode parentName="p">{`ENTER`}</inlineCode>{` choosing a suggestion and `}<inlineCode parentName="p">{`ESCAPE`}</inlineCode>{` allowing the user to exit the type-ahead menu without selecting anything.`}</p>
    <h3>{`Faceted filtering`}</h3>
    <p>{`Users should be able to `}<inlineCode parentName="p">{`TAB`}</inlineCode>{` into the filter panel to cycle through the different groups. Pressing `}<inlineCode parentName="p">{`ENTER`}</inlineCode>{` moves the selection to the facets inside a group, allowing the user to move through the facets with the `}<inlineCode parentName="p">{`ARROW`}</inlineCode>{` keys. Pressing `}<inlineCode parentName="p">{`ENTER`}</inlineCode>{` while a facet is in focus would toggle select or deselect the facet.`}</p>
    <p>{`After a facet is selected or deselected, the focus state should be retained as the content reloads, that way users don’t have to `}<inlineCode parentName="p">{`TAB`}</inlineCode>{` through everything again as they move down the list.`}</p>
    <h2>{`Related`}</h2>
    <Row mdxType="Row">
      <Column colSm={2} colMd={2} colLg={4} mdxType="Column">
        <h4>{`Components`}</h4>
        <ul>
          <li parentName="ul"><a parentName="li" {...{
              "href": "/components/checkbox/usage"
            }}>{`Checkbox`}</a></li>
          <li parentName="ul"><a parentName="li" {...{
              "href": "/components/date-picker/usage"
            }}>{`Date picker`}</a></li>
          <li parentName="ul"><a parentName="li" {...{
              "href": "/components/modal/usage"
            }}>{`Modal`}</a></li>
          <li parentName="ul"><a parentName="li" {...{
              "href": "/components/pagination/usage"
            }}>{`Pagination`}</a></li>
          <li parentName="ul"><a parentName="li" {...{
              "href": "/components/search/usage"
            }}>{`Search`}</a></li>
          <li parentName="ul"><a parentName="li" {...{
              "href": "/components/button/usage#icon-usage"
            }}>{`Select`}</a></li>
          <li parentName="ul"><a parentName="li" {...{
              "href": "/components/text-input/usage"
            }}>{`Text input`}</a></li>
          <li parentName="ul"><a parentName="li" {...{
              "href": "/components/ui-shell-header/usage"
            }}>{`UI shell`}</a></li>
        </ul>
      </Column>
      <Column colSm={2} colMd={2} colLg={4} mdxType="Column">
        <h4>{`Patterns`}</h4>
        <ul>
          <li parentName="ul"><a parentName="li" {...{
              "href": "/patterns/empty-states-pattern"
            }}>{`Empty states`}</a></li>
          <li parentName="ul"><a parentName="li" {...{
              "href": "/patterns/filtering"
            }}>{`Filtering`}</a></li>
        </ul>
      </Column>
    </Row>
    <h2>{`References`}</h2>
    <ul>
      <li parentName="ul">{`Apple Human Interface Guidelines, `}<a parentName="li" {...{
          "href": "https://developer.apple.com/design/human-interface-guidelines/macos/fields-and-labels/search-fields/"
        }}>{`Search Fields`}</a>{` (2019)`}</li>
      <li parentName="ul">{`Nick Babich, `}<a parentName="li" {...{
          "href": "https://uxplanet.org/best-practices-for-search-results-1bbed9d7a311"
        }}>{`Best Practices for Search Results`}</a>{` (2017)`}</li>
      <li parentName="ul">{`Think with Google, `}<a parentName="li" {...{
          "href": "https://www.thinkwithgoogle.com/marketing-resources/experience-design/chapter-2-in-app-search/"
        }}>{`In-App Search`}</a>{` (2016)`}</li>
    </ul>
    <h2>{`Feedback`}</h2>
    <p>{`Help us improve this pattern by providing feedback, asking questions, and leaving any other comments `}<a parentName="p" {...{
        "href": "https://github.com/carbon-design-system/carbon-website/issues/new?assignees=&labels=feedback&template=feedback.md"
      }}>{`on GitHub`}</a>{`.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      